import { type ComputedArrivalData } from "@src/appV2/redesign/ArrivalTime/computeArrivalData";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { type ShiftInvite } from "../types";

const SHIFT_INVITE_SNAPSHOT_QUERY_KEY = "shiftInviteSnapshot";

interface ShiftInviteSnapshotPayload {
  shiftInvite: ShiftInvite;
  computedArrivalData: ComputedArrivalData;
}

/**
 * Hook for managing shift invite snapshots in React Query cache
 * Provides synchronous access to cached shift invite data
 */
export function useShiftInviteSnapshot(shiftInviteId: string) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => [SHIFT_INVITE_SNAPSHOT_QUERY_KEY, shiftInviteId], [shiftInviteId]);

  const getShiftInviteSnapshot = useCallback((): ShiftInviteSnapshotPayload | undefined => {
    return queryClient.getQueryData<ShiftInviteSnapshotPayload>(queryKey);
  }, [queryClient, queryKey]);

  const setShiftInviteSnapshot = useCallback(
    (payload: ShiftInviteSnapshotPayload) => {
      queryClient.setQueryData<ShiftInviteSnapshotPayload>(queryKey, payload);
    },
    [queryClient, queryKey]
  );

  const clearShiftInviteSnapshot = useCallback(() => {
    queryClient.removeQueries({ queryKey });
  }, [queryClient, queryKey]);

  return {
    shiftInviteSnapshot: getShiftInviteSnapshot(),
    setShiftInviteSnapshot,
    clearShiftInviteSnapshot,
  };
}
